import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { FilterSettingsModel, GridComponent, GridModule, PageSettingsModel } from '@syncfusion/ej2-angular-grids';
import { DateTimePickerModule } from '@syncfusion/ej2-angular-calendars';
import { DataManager } from '@syncfusion/ej2-data';
import { DateTime } from 'luxon';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import {
  FilterCondition,
  FilterDTO,
  FilterOperator,
  LogEventLevel,
  ProblemReportOrigin,
  ProblemReportResponseDTO,
} from '../../../../base/models/models.swagger.generated';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';
import { EnumDropDownListComponent } from 'src/app/base/shared/enum-drop-down-list/enum-drop-down-list.component';
import { AvpCustomAdaptor } from 'src/app/base/shared/syncfusion/avp-custom-adaptor';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ProblemReportLogsService } from './problem-report-logs.service';

@Component({
  selector: 'app-problem-report-detail',
  templateUrl: './problem-report-detail.component.html',
  styleUrls: ['./problem-report-detail.component.scss'],
  imports: [
    GridModule,
    TranslateModule,
    ReactiveFormsModule,
    DateTimePickerModule,
    EnumDropDownListComponent,
  ]
})
export class ProblemReportDetailComponent implements OnInit, OnDestroy {

  @ViewChild('logsGrid') public logsGrid: GridComponent;
  @Input() public report: ProblemReportResponseDTO;

  public logsData: DataManager;
  public logsPageSettings: PageSettingsModel = GridConstantsFactory.createStandardPageSettings();
  public logsFilterSettings: FilterSettingsModel = GridConstantsFactory.createStandardFilterSettings();
  public LogEventLevel = LogEventLevel;
  public ProblemReportOrigin = ProblemReportOrigin;

  public filterForm: FormGroup;

  private destroy$ = new Subject<void>();

  constructor(
    private problemReportLogsService: ProblemReportLogsService,
    private spinnerService: SpinnerService,
    private fb: FormBuilder,
  ) {
    this.filterForm = this.fb.group({
      level: [null],
      searchText: [''],
      startDate: [null],
      endDate: [null],
    });
  }

  public ngOnInit(): void {
    this.problemReportLogsService.problemReportId = this.report.id;
    this.problemReportLogsService.additionalFilters = [];

    this.logsData = new DataManager({
      adaptor: new AvpCustomAdaptor(this.problemReportLogsService, this.spinnerService),
    });

    this.filterForm.valueChanges
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => {
        this.problemReportLogsService.additionalFilters = this.buildAdditionalFilters();
        this.logsGrid?.refresh();
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public formatDateTime(dateTime: DateTime): string {
    return dateTime ? dateTime.toFormat('dd.MM.yyyy HH:mm:ss') : '';
  }

  public formatLogLevel(level: LogEventLevel): string {
    return LogEventLevel[level] ?? '';
  }

  public getLevelCssClass(level: LogEventLevel): string {
    switch (level) {
      case LogEventLevel.Verbose: return 'log-level-verbose';
      case LogEventLevel.Debug: return 'log-level-debug';
      case LogEventLevel.Information: return 'log-level-information';
      case LogEventLevel.Warning: return 'log-level-warning';
      case LogEventLevel.Error: return 'log-level-error';
      case LogEventLevel.Fatal: return 'log-level-fatal';
      default: return '';
    }
  }

  public copyToClipboard(text: string): void {
    if (!text) {
      return;
    }
    navigator.clipboard.writeText(text);
  }

  private buildAdditionalFilters(): FilterDTO[] {
    const filters: FilterDTO[] = [];
    const { level, searchText, startDate, endDate } = this.filterForm.value;

    if (level != null) {
      filters.push(new FilterDTO({
        isComplex: false,
        field: 'Level',
        operator: FilterOperator.Equal,
        value: level.toString(),
      }));
    }

    if (searchText?.trim()) {
      filters.push(new FilterDTO({
        isComplex: true,
        condition: FilterCondition.Or,
        predicates: [
          new FilterDTO({ isComplex: false, field: 'Message', operator: FilterOperator.Contains, value: searchText.trim() }),
          new FilterDTO({ isComplex: false, field: 'Exception', operator: FilterOperator.Contains, value: searchText.trim() }),
        ],
      }));
    }

    if (startDate) {
      filters.push(new FilterDTO({
        isComplex: false,
        field: 'Timestamp',
        operator: FilterOperator.GreaterThanOrEqual,
        value: DateTime.fromJSDate(startDate).toISO(),
      }));
    }

    if (endDate) {
      filters.push(new FilterDTO({
        isComplex: false,
        field: 'Timestamp',
        operator: FilterOperator.LessThanOrEqual,
        value: DateTime.fromJSDate(endDate).toISO(),
      }));
    }

    return filters;
  }
}
